/**
 * AVS-4511 prototype switch: `?voipfx=noise`, `?voipfx=blur` or `?voipfx=noise,blur` runs the
 * in-app noise filter and background blur on our outgoing VOIP tracks.
 *
 * Never in prod. The check is on the build-time STAGE literal, not isDebugMode() (`?dev` turns
 * that on in prod too), so a prod build drops the pipeline chunk and its assets entirely.
 * Without the parameter nothing is imported and getUserMedia is untouched.
 */
import log from 'utils/log';
import type { INoiseOptions, TNoiseFilter } from './audioProcessor';
import type { TBlurBackground } from './videoProcessor';

export type TVoipFx = 'noise' | 'blur';

const NONE: ReadonlySet<TVoipFx> = new Set();

export function parseVoipFx(value: string | null, stage: string | undefined): ReadonlySet<TVoipFx> {
  if (stage === 'prod' || !value) return NONE;
  const fx = value
    .split(',')
    .map((item) => item.trim())
    .filter((item): item is TVoipFx => item === 'noise' || item === 'blur');
  return new Set(fx);
}

export interface IVoipFxOptions {
  noise: INoiseOptions;
  background: TBlurBackground;
}

export const DEFAULT_GATE_DB = -50;

const NOISE_FILTERS: readonly TNoiseFilter[] = ['gtcrn', 'rnnoise', 'speex'];
const BACKGROUNDS: readonly TBlurBackground[] = ['blur', 'blur-strong', 'white'];

function oneOf<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.find((item) => item === value) ?? fallback;
}

/** `voipgate=on` uses the default; a number is the open threshold in dB, clamped to [−100, 0]. */
function parseGateDb(value: string | null): number | undefined {
  if (value === null || value === '' || value === 'off') return undefined;
  if (value === 'on') return DEFAULT_GATE_DB;
  const db = Number(value);
  return Number.isFinite(db) ? Math.min(0, Math.max(-100, db)) : undefined;
}

/** `?voipnoise=gtcrn|rnnoise|speex` (GTCRN by default), `?voipgate=on|<dB>`, `?voipbg=blur|blur-strong|white`. */
export function parseVoipFxOptions(params: URLSearchParams): IVoipFxOptions {
  return {
    noise: {
      filter: oneOf(params.get('voipnoise'), NOISE_FILTERS, 'gtcrn'),
      gateDb: parseGateDb(params.get('voipgate')),
    },
    background: oneOf(params.get('voipbg'), BACKGROUNDS, 'blur'),
  };
}

let options: IVoipFxOptions | undefined;

/** Read once per page load. */
export function voipFxOptions(): IVoipFxOptions {
  // Prod never runs a filter; the literal check lets the build drop the parser.
  if (process.env.STAGE === 'prod') return { noise: {}, background: 'blur' };
  options ??= parseVoipFxOptions(new URLSearchParams(window.location.search));
  return options;
}

let requested: ReadonlySet<TVoipFx> | undefined;

/** Read once per page load. */
export function requestedVoipFx(): ReadonlySet<TVoipFx> {
  requested ??= parseVoipFx(new URLSearchParams(window.location.search).get('voipfx'), process.env.STAGE);
  return requested;
}

/**
 * With the noise filter on, the browser's own suppression is off so two filters do not fight.
 * Echo cancellation and gain control stay on.
 */
export function withVoipFxConstraints(
  constraints: MediaStreamConstraints,
  fx: ReadonlySet<TVoipFx>
): MediaStreamConstraints {
  if (!fx.has('noise') || !constraints.audio) return constraints;
  const audio = typeof constraints.audio === 'object' ? constraints.audio : {};
  return {
    ...constraints,
    audio: { ...audio, noiseSuppression: false, echoCancellation: true, autoGainControl: true },
  };
}

// How long a grab waits for the filters to finish loading. Past it the call starts with plain
// tracks (browser noise suppression on) and the filters apply from the next re-grab.
export const VOIP_FX_GRACE_MS = 2000;

let pipeline: Promise<typeof import('./fxPipeline')> | undefined;
let preloading: Promise<void> | undefined;
// Filters whose preload finished and passed. Each one lands on its own, so a slow blur warm-up
// never holds the noise filter back.
const loaded = new Set<TVoipFx>();

function loadPipeline() {
  pipeline ??= import('./fxPipeline');
  return pipeline;
}

/**
 * Starts loading and checking the requested filters, once per page. Call it early (the media
 * manager does on creation) so the first grab finds them ready.
 */
export function preloadVoipFx(fx: ReadonlySet<TVoipFx> = requestedVoipFx()): Promise<void> {
  if (process.env.STAGE === 'prod' || fx.size === 0) return Promise.resolve();
  preloading ??= loadPipeline()
    .then(({ prepareVoipFx }) =>
      Promise.all(
        [...fx].map((name) =>
          prepareVoipFx(new Set([name]), voipFxOptions()).then((ready) => {
            if (ready.has(name)) loaded.add(name);
          })
        )
      )
    )
    .then(() => undefined)
    .catch((error: unknown) => {
      log.warn('[voipfx] pipeline unavailable, sending raw tracks', error);
    });
  return preloading;
}

/** The requested filters that are ready, waiting at most the grace period for the rest. */
function readyWithin(fx: ReadonlySet<TVoipFx>, graceMs: number): Promise<ReadonlySet<TVoipFx>> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, graceMs);
  });
  return Promise.race([preloadVoipFx(fx), late]).then(() => {
    clearTimeout(timer);
    const ready = new Set([...fx].filter((name) => loaded.has(name)));
    if (ready.size < fx.size)
      log.warn(`[voipfx] not ready after ${graceMs} ms: ${[...fx].filter((n) => !ready.has(n))}`);
    return ready;
  });
}

/**
 * getUserMedia with the requested filters. The browser's noise suppression goes off only when our
 * filter loaded, and a grab never waits more than the grace period for the download. If the filter
 * still fails on this track, the mic is reopened with the browser's suppression back on.
 */
export async function getUserMediaWithVoipFx(
  constraints: MediaStreamConstraints,
  fx: ReadonlySet<TVoipFx>,
  graceMs = VOIP_FX_GRACE_MS
): Promise<MediaStream> {
  const loaded = fx.size ? await readyWithin(fx, graceMs) : NONE;
  const pipeline = loaded.size ? await loadPipeline() : undefined;
  const ready = pipeline ? pipeline.workingVoipFx(loaded) : NONE;
  const raw = await navigator.mediaDevices.getUserMedia(withVoipFxConstraints(constraints, ready));
  if (!pipeline || ready.size === 0) return raw;

  const processed = await pipeline.processStream(raw, ready, voipFxOptions());
  const [rawMic] = raw.getAudioTracks();
  if (!ready.has('noise') || !rawMic || !processed.getAudioTracks().includes(rawMic)) return processed;

  // Our filter failed on this track and Chrome ignores turning suppression back on in place.
  const reopened = await navigator.mediaDevices.getUserMedia({ audio: constraints.audio }).catch((error: unknown) => {
    log.warn('[voipfx] could not reopen the mic with browser noise suppression', error);
    return undefined;
  });
  if (!reopened) return processed;
  rawMic.stop();
  return new MediaStream([...reopened.getAudioTracks(), ...processed.getVideoTracks()]);
}
